import { supabase } from './supabase';

// AWS Lambda API base URL from environment variable
const AWS_API_BASE_URL = import.meta.env.VITE_AWS_API_BASE_URL || 'https://api.octalchip.com';

// Add debounce mechanism to prevent rapid API calls
let lastCreditsCall = 0;
const CREDITS_CALL_DEBOUNCE = 1000; // 1 second debounce

const emailValidationService = {
  // Validate a single email address
  validateSingleEmail: async (email) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      // For demo purposes, allow validation without authentication
      // In production, you would require authentication
      if (!session) {
        // Return a demo response for non-authenticated users
        return { 
          success: true, 
          data: {
            email,
            is_valid: Math.random() > 0.3, // 70% valid for demo
            score: Math.floor(Math.random() * 100),
            status: Math.random() > 0.3 ? 'valid' : 'invalid',
            message: Math.random() > 0.3 ? 'Email is valid' : 'Email is invalid',
            demo: true
          }
        };
      }

      const response = await fetch(`${AWS_API_BASE_URL}/emailvalidator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          emails: [email], 
          validationType: 'single' 
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Single email validation failed' };
      }

      // Extract single email result from the response
      const singleEmailResult = result.emailResults ? result.emailResults[0] : null;
      
      return { 
        success: true, 
        data: singleEmailResult || result, 
        creditsUsed: result.creditsUsed,
        creditsRemaining: result.creditsRemaining,
        creditsTotal: result.creditsTotal,
        creditsUsedTotal: result.creditsUsedTotal,
        credits: result.credits // Include the credits object from the response
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Validate multiple email addresses
  validateBulkEmails: async (emails, validationType = 'bulk') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      // console.log('validateBulkEmails - Session:', session); // Debug log
      // console.log('validateBulkEmails - Emails:', emails); // Debug log
      // console.log('validateBulkEmails - ValidationType:', validationType); // Debug log
      
      // For demo purposes, allow validation without authentication
      if (!session) {
        // Return a demo response for non-authenticated users
        const demoResults = emails.map(email => ({
          email,
          is_valid: Math.random() > 0.3,
          score: Math.floor(Math.random() * 100),
          status: Math.random() > 0.3 ? 'valid' : 'invalid',
          message: Math.random() > 0.3 ? 'Email is valid' : 'Email is invalid'
        }));
        
        const validCount = demoResults.filter(r => r.is_valid).length;
        const invalidCount = demoResults.filter(r => !r.is_valid).length;
        const riskyCount = Math.floor(invalidCount * 0.1);
        const unknownCount = Math.floor(emails.length * 0.05);
        
        return { 
          success: true, 
          data: {
            validationId: 'demo-' + Date.now(),
            totalEmails: emails.length,
            processedEmails: emails.length,
            results: {
              valid: validCount,
              invalid: invalidCount,
              risky: riskyCount,
              unknown: unknownCount
            },
            processingTime: emails.length * 200, // 200ms per email
            demo: true
          }
        };
      }

      // console.log('Making real API call to AWS Lambda...'); // Debug log
      // console.log('API URL:', `${AWS_API_BASE_URL}/emailvalidator`); // Debug log
      // console.log('Request body:', { emails, validationType }); // Debug log
      
      const response = await fetch(`${AWS_API_BASE_URL}/emailvalidator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ emails, validationType })
      });

      const result = await response.json();
      // console.log('API Response status:', response.status); // Debug log
      // console.log('API Response:', result); // Debug log
      
      if (!response.ok) {
        // console.log('API call failed:', result.error); // Debug log
        return { success: false, error: result.error || 'Bulk email validation failed' };
      }

      // console.log('API call successful, returning data with updated credits'); // Debug log
      return { 
        success: true, 
        data: {
          ...result,
          creditsUsed: result.creditsUsed,
          creditsRemaining: result.creditsRemaining,
          creditsTotal: result.creditsTotal,
          creditsUsedTotal: result.creditsUsedTotal
        },
        credits: result.credits // Include the credits object from the response
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get validation progress
  getValidationProgress: async (validationId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      const response = await fetch(`${AWS_API_BASE_URL}/validation-progress?validationId=${validationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to get validation progress' };
      }

      return { success: true, data: result };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get validation results
  getValidationResults: async (validationId, options = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Handle demo mode for non-authenticated users
      if (!session) {
        // For demo mode, return mock results
        const mockResults = [
          {
            email: 'test@example.com',
            status: 'valid',
            deliverabilityScore: 85,
            domain: 'example.com',
            mxRecord: true,
            disposable: false,
            freeProvider: false,
            roleBased: false,
            catchAll: false,
            processingTimeMs: 150
          },
          {
            email: 'user@gmail.com',
            status: 'valid',
            deliverabilityScore: 90,
            domain: 'gmail.com',
            mxRecord: true,
            disposable: false,
            freeProvider: true,
            roleBased: false,
            catchAll: false,
            processingTimeMs: 120
          }
        ];
        
        return { 
          success: true, 
          data: {
            results: mockResults,
            total: mockResults.length,
            page: 1,
            limit: options.limit || 25
          }
        };
      }

      const queryParams = new URLSearchParams({
        validationId,
        page: options.page || 1,
        limit: options.limit || 25,
        sortBy: options.sortBy || 'created_at',
        sortOrder: options.sortOrder || 'desc'
      });

      if (options.status && options.status !== 'all') {
        queryParams.append('status', options.status);
      }

      const response = await fetch(`${AWS_API_BASE_URL}/validation-results?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to get validation results' };
      }

      return { success: true, data: result };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get validation history
  getValidationHistory: async (options = {}) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      let query = supabase
        .from('email_validations')
        .select('*', { count: 'exact' })
        .eq('user_id', session.user.id);

      // Apply filters
      if (options.status && options.status !== 'all') {
        query = query.eq('status', options.status);
      }

      if (options.validationType && options.validationType !== 'all') {
        query = query.eq('validation_type', options.validationType);
      }

      if (options.dateFrom) {
        query = query.gte('created_at', options.dateFrom);
      }

      if (options.dateTo) {
        query = query.lte('created_at', options.dateTo);
      }

      // Apply sorting
      const sortBy = options.sortBy || 'created_at';
      const sortOrder = options.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const page = options.page || 1;
      const limit = options.limit || 10;
      const offset = (page - 1) * limit;
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          validations: data || [],
          pagination: {
            currentPage: page,
            totalPages: Math.ceil((count || 0) / limit),
            totalResults: count || 0,
            limit,
            hasNextPage: page < Math.ceil((count || 0) / limit),
            hasPrevPage: page > 1
          }
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update user credits in database
  updateUserCredits: async (creditsUsed) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      // Update credits in database
      const { data, error } = await supabase
        .from('email_validation_credits')
        .update({
          credits_used: supabase.sql`credits_used + ${creditsUsed}`,
          credits_remaining: supabase.sql`GREATEST(0, credits_remaining - ${creditsUsed})`,
          last_updated: new Date().toISOString()
        })
        .eq('user_id', session.user.id)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get user credits from database
  getUserCredits: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Return demo credits for non-authenticated users
        return { 
          success: true, 
          data: {
            credits: 100,
            credits_total: 100,
            credits_used: 0,
            credits_remaining: 100,
            demo: true
          }
        };
      }

      // Get real credits from database
      const { data: creditData, error: creditError } = await supabase
        .from('email_validation_credits')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      if (creditError && creditError.code === 'PGRST116') {
        // No credits record exists, create one with 100 credits
        const { data: newCredits, error: createError } = await supabase
          .from('email_validation_credits')
          .insert({
            user_id: session.user.id,
            credits_total: 100,
            credits_used: 0,
            credits_remaining: 100,
            subscription_plan: 'free',
            plan_limit: 100,
            reset_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          })
          .select()
          .single();

        if (createError) {
          return { success: false, error: createError.message };
        }

        return { success: true, data: newCredits };
      } else if (creditError) {
        return { success: false, error: creditError.message };
      }

      return { success: true, data: creditData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get current credit information using API call with empty emails array
  getCurrentCredits: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Return demo credits for non-authenticated users
        return { 
          success: true, 
          data: {
            credits_remaining: 100,
            credits_total: 100,
            credits_used: 0,
            subscription_plan: 'free',
            billing_cycle: 'monthly',
            is_unlimited: false,
            demo: true
          }
        };
      }

      // Check debounce to prevent rapid successive calls
      const now = Date.now();
      if (now - lastCreditsCall < CREDITS_CALL_DEBOUNCE) {
        // console.log('⏳ Debouncing credits call - too soon after last call');
        return { success: false, error: 'Please wait before making another request' };
      }
      
      lastCreditsCall = now;
      // console.log('Getting current credits via API call...');
      
      const response = await fetch(`${AWS_API_BASE_URL}/emailvalidator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          emails: [], 
          validationType: 'bulk' 
        })
      });

      const result = await response.json();
      // console.log('Current credits API response:', result);
      
      if (!response.ok) {
        // console.log('API call failed:', result.error);
        return { success: false, error: result.error || 'Failed to get current credits' };
      }

      // Extract credit information from the response
      const creditInfo = {
        credits_remaining: result.creditsRemaining || 0,
        credits_total: result.creditsTotal || 0,
        credits_used: result.creditsUsedTotal || 0,
        subscription_plan: result.subscription_plan || 'free',
        billing_cycle: 'monthly', // Default value
        is_unlimited: false // Default value
      };

      // console.log('Extracted credit info:', creditInfo);
      
      return { 
        success: true, 
        data: creditInfo
      };
    } catch (error) {
      // console.error('Error getting current credits:', error);
      return { success: false, error: error.message };
    }
  },

  // Get validation statistics
  getValidationStatistics: async (timeRange = '30d') => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Return demo statistics for non-authenticated users
        return { 
          success: true, 
          data: {
            timeRange,
            totalValidations: 8,
            totalEmails: 45,
            totalProcessed: 45,
            totalValid: 32,
            totalInvalid: 10,
            totalRisky: 2,
            totalUnknown: 1,
            validRate: 71,
            invalidRate: 22,
            completedValidations: 8,
            failedValidations: 0
          }
        };
      }

      // Get user credits for display
      const { data: creditData, error: creditError } = await supabase
        .from('email_validation_credits')
        .select('credits_remaining, subscription_plan, credits_total, credits_used')
        .eq('user_id', session.user.id)
        .single();

      if (creditError && creditError.code === 'PGRST116') {
        // No credits exist, create them
        const { data: newCredits, error: createError } = await supabase
          .from('email_validation_credits')
          .insert({
            user_id: session.user.id,
            credits_total: 100,
            credits_used: 0,
            credits_remaining: 100,
            subscription_plan: 'free',
            plan_limit: 100,
            reset_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          })
          .select('credits_remaining, subscription_plan, credits_total, credits_used')
          .single();

        if (createError) {
          return { 
            success: true, 
            data: {
              credits_remaining: 100,
              credits_total: 100,
              credits_used: 0,
              subscription_plan: 'free'
            }
          };
        } else {
          return { 
            success: true, 
            data: newCredits
          };
        }
      } else if (creditError) {
        return { 
          success: true, 
          data: {
            credits_remaining: 100,
            credits_total: 100,
            credits_used: 0,
            subscription_plan: 'free'
          }
        };
      }

      return { 
        success: true, 
        data: creditData
      };
    } catch (error) {
      // console.error('Statistics error:', error);
      return { success: false, error: error.message };
    }
  },

  // Initialize user credits (for existing users)
  initializeUserCredits: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Authentication required' };
      }

      // Check if user already has credits
      const { data: existingCredits } = await supabase
        .from('email_validation_credits')
        .select('id')
        .eq('user_id', session.user.id)
        .single();

      if (existingCredits) {
        return { success: true, data: existingCredits, message: 'Credits already exist' };
      }

      // Create new credits for the user
      const { data, error } = await supabase
        .from('email_validation_credits')
        .insert({
          user_id: session.user.id,
          credits_total: 100,
          credits_used: 0,
          credits_remaining: 100,
          subscription_plan: 'free',
          plan_limit: 100,
          reset_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data, message: 'Credits initialized successfully' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Parse email file content
  parseEmailFile: (fileContent, fileType) => {
    try {
      let emails = [];
      
      if (fileType === 'csv') {
        // Simple CSV parsing - assumes emails are in first column or comma-separated
        const lines = fileContent.split('\n');
        lines.forEach(line => {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            // Try to extract email from CSV line
            const emailMatch = trimmedLine.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
            if (emailMatch) {
              emails.push(emailMatch[1]);
            }
          }
        });
      } else if (fileType === 'txt') {
        // Simple text file parsing - one email per line
        const lines = fileContent.split('\n');
        lines.forEach(line => {
          const trimmedLine = line.trim();
          if (trimmedLine && trimmedLine.includes('@')) {
            emails.push(trimmedLine);
          }
        });
      }
      
      // Remove duplicates and validate format
      const uniqueEmails = [...new Set(emails)];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const validEmails = uniqueEmails.filter(email => emailRegex.test(email));
      
      return {
        success: true,
        data: {
          emails: validEmails,
          totalFound: uniqueEmails.length,
          validEmails: validEmails.length,
          duplicatesRemoved: emails.length - uniqueEmails.length,
          invalidEmails: uniqueEmails.length - validEmails.length
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

export default emailValidationService;